// StoryEditor — edit a story board in place (#1416).
//
// The page is the web app itself, in a same-origin iframe, so it looks
// exactly as a reader sees it. Click a heading or a paragraph and type.
// Figures, charts and tables are locked (they come from the data; the agent
// changes them), and say where they come from on hover. A box with named
// inputs (the opening, a section, a callout, a picture) has a Settings chip
// that edits every input, including the ones not typed in place (a picture,
// a tone, a link). Each section has a
// small toolbar to move, hide, duplicate or delete it; hovering or focusing
// the toolbar outlines the whole section it acts on. Save keeps a draft;
// Publish is the separate step that changes what people with the link see.
//
// The host draws the editor full screen (Adam, 27 Sep) and puts its agent
// panel in the `aside` slot, over the right of the page, below the bar.
//
// Page → hook → component: every request lives in useStoryDraft; this
// component takes its result as `draft` and only calls its callbacks. The
// source engine (storySource.ts) and the iframe bridge (storyFrame.ts) are
// pure and tested on their own.

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import HistoryIcon from '@mui/icons-material/History'
import CloseIcon from '@mui/icons-material/Close'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import TuneIcon from '@mui/icons-material/Tune'
import {
  blockInputs,
  blockNeighbour,
  blockOf,
  deleteBlock,
  duplicateBlock,
  hrefOk,
  imageOk,
  isHidden,
  isLeafTag,
  isMovableBlock,
  leafOrdinalName,
  moveBlock,
  outerSource,
  parseStory,
  setHidden,
  setInputs,
  settingsHostOf,
  startTagSource,
  STORY_VOCABULARY,
  type BlockInput,
  type StoryDoc,
} from '../story/storySource.js'
import {
  FIELD_ATTR,
  HOST_ATTR,
  IDX_ATTR,
  ROLE_ATTR,
  clearSpotlight,
  commitField,
  commitProse,
  idxOf,
  markDirty,
  mountStoryForEditing,
  proseBaseline,
  sectionLabel,
  setContentEditable,
  shouldBlockInput,
  spotlightSection,
  targetsOf,
  type ProseBaseline,
  type StoryRuntime,
} from '../story/storyFrame.js'
import type { StoryVersion, UseStoryDraftResult } from '../hooks/useStoryDraft.js'
import { CARD_RADIUS, HAIRLINE, HAIRLINE_HOVER_COLOR, PILL_RADIUS } from './surface.js'

/** Longest element source quoted to the agent. */
export const ASK_EXCERPT_MAX = 1500

/** What "Ask the agent about this" is about: one element of story.html. */
export interface StoryAskTarget {
  /** Ordinal of the element in story.html. */
  idx: number
  tag: string
  /** The element's source (trimmed to ASK_EXCERPT_MAX). */
  excerpt: string
  /** Said to the reader: "Chart: Would consider, %", "Paragraph: “Over the summer…”". */
  label: string
}

/**
 * The message the agent receives for "Ask the agent about this". `elementName`
 * (e.g. "Chart 2", from `storyElementLabel`/`leafOrdinalName`) is stated on
 * its own first line, when known, so the panel can name the element in its
 * row instead of "the selected element" (storyPanelRows.ts's `ASK_PROMPT`
 * accepts the message with or without it).
 */
export function buildStoryAskPrompt(excerpt: string, instruction: string, elementName?: string): string {
  const head = elementName ? `Story board element: ${elementName}\n` : ''
  return `${head}Story board element (in story.html):\n${excerpt}\n\nInstruction: ${instruction.trim()}`
}

export function storyExcerpt(doc: StoryDoc, idx: number, headOnly = false): string {
  const src = headOnly ? startTagSource(doc, idx) : outerSource(doc, idx)
  return src.length > ASK_EXCERPT_MAX ? `${src.slice(0, ASK_EXCERPT_MAX)}…` : src
}

const ELEMENT_NAMES: Record<string, string> = {
  'pt-num': 'Figure', 'pt-kpi': 'Figure', 'pt-chart': 'Chart', 'pt-table': 'Table',
  'pt-callout': 'Callout', 'pt-figure': 'Picture', 'pt-text': 'Text',
  p: 'Paragraph', li: 'Bullet', h1: 'Heading', h2: 'Heading', h3: 'Heading', h4: 'Heading', blockquote: 'Quote',
}

const FIELD_NAMES: Record<string, string> = {
  title: 'Title', subtitle: 'Subtitle', verdict: 'Verdict', label: 'KPI label', meta: 'Meta line', button: 'Button',
  caption: 'Caption', cite: 'Attribution', 'change-label': 'Change label', 'compare-label': 'Comparison label',
}

/** What each named input is called in a box's settings. */
const INPUT_LABELS: Record<string, string> = {
  title: 'Title', subtitle: 'Subtitle', meta: 'Meta line', verdict: 'Verdict', bar: 'Title beside the logo',
  button: 'Button', 'button-href': 'Button goes to', image: 'Picture behind', tone: 'Tone', kind: 'Kind',
  cite: 'Attribution', src: 'Picture', alt: 'Description for screen readers', caption: 'Caption',
  label: 'Label', 'change-label': 'Words after the change', 'compare-label': 'Compared with',
}

/** A box's name in its Settings chip and dialog: "Callout settings". */
const SETTINGS_NAMES: Record<string, string> = {
  'pt-hero': 'Opening', 'pt-section': 'Section', 'pt-text': 'Text', 'pt-callout': 'Callout', 'pt-figure': 'Picture',
  'pt-kpi': 'Figure', 'pt-chart': 'Chart', 'pt-table': 'Table',
}

const HREF_HINT = 'A #section-id, a page in the app, or an https:// or mailto: link'
const IMAGE_HINT = 'A picture file in the app, e.g. assets/hero.webp'

const quoted = (text: string) => {
  const words = text.replace(/\s+/g, ' ').trim()
  return words.length > 40 ? `“${words.slice(0, 40).trimEnd()}…”` : `“${words}”`
}

/**
 * How the reader sees an element named: its kind, then its title or the start
 * of its words. `field` names an attribute being edited on it (a heading, a
 * subtitle, a KPI label), with `text` its words.
 */
export function storyElementLabel(doc: StoryDoc, idx: number, text = '', field?: string): string {
  const node = doc.nodes[idx]
  if (!node) return 'Element'
  if (field) {
    const name = field === 'title' && node.tag === 'pt-section' ? 'Section heading' : FIELD_NAMES[field] ?? 'Field'
    return text.trim() ? `${name}: ${quoted(text)}` : name
  }
  if (node.tag === 'pt-section' || (node.parent === -1 && node.tag !== 'pt-hero')) return sectionLabel(doc, idx)
  if (node.tag === 'pt-hero') return 'Opening'
  const kind = ELEMENT_NAMES[node.tag] ?? 'Element'
  const title = (node.attrs.title ?? node.attrs.label ?? '').trim()
  if (title) return `${kind}: ${title}`
  if (isLeafTag(node.tag)) return leafOrdinalName(doc, idx) ?? kind
  return text.trim() ? `${kind}: ${quoted(text)}` : kind
}

export interface StoryEditorProps {
  /** The web app's entry page, served same-origin (`${apiBaseUrl}/webapp/${sessionId}/${token}/${entry}`). */
  frameSrc: string
  /** What useStoryDraft returns. */
  draft: UseStoryDraftResult
  /**
   * Open the agent: about one element ("Ask the agent about this", "Change
   * via agent" on a locked figure), or with nothing selected (null). The host
   * owns the conversation. Absent: the editor offers no agent actions.
   */
  onAskAgent?: (target: StoryAskTarget | null) => void
  /** The agent panel is open: the bar's button reads as pressed. */
  askOpen?: boolean
  onClose?: () => void
  /** Drawn over the right of the page area, below the bar: the host's agent panel. */
  aside?: ReactNode
  /** Host notes, above the editor's own messages (e.g. "The agent is working"). */
  notices?: ReactNode
  /**
   * While `askOpen`, the width the `aside` covers (any CSS width, e.g. a
   * clamp()). The page area gives up that much on the right, at once and with
   * no transition, so the page reflows once and stays reachable beside the
   * panel rather than under it.
   */
  asideWidth?: string
  /** Shown in the bar. Default: "Story board". */
  title?: string
  /** The story runtime to hydrate with. Default: the page's own lib/story.js. Tests pass a stand-in. */
  runtime?: StoryRuntime
  /** Reopen the five-step explainer (#1578 item 10). Absent: no help icon. */
  onHelp?: () => void
  /**
   * Publish when the story board is not in DataViz yet (#1578 item 13): the
   * host opens its publish dialog, which asks for a name and share settings.
   * Absent, Publish only updates the copies that exist.
   */
  onPublishNew?: () => void
}

/** `field`: which attribute a `field` selection edits (title, subtitle, verdict, label). */
type Selection = { idx: number; kind: 'prose' | 'field' | 'leaf' | 'block'; field?: string }

function formatWhen(v: string | number): string {
  const ms = typeof v === 'number' ? (v < 1e12 ? v * 1000 : v) : Date.parse(v)
  if (!Number.isFinite(ms)) return String(v)
  return new Date(ms).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

const isMod = (e: { metaKey: boolean; ctrlKey: boolean }) => e.metaKey || e.ctrlKey

// `clip` where the browser has it: unlike `hidden` it is not a scroll box, so
// no scrollIntoView or focus can move it. `hidden` is the fallback.
const NO_SCROLL = { overflow: 'hidden', '@supports (overflow: clip)': { overflow: 'clip' } } as const
const NO_SIDEWAYS_SCROLL = { overflowX: 'hidden', '@supports (overflow: clip)': { overflowX: 'clip' } } as const

/** A text field of the host's (not the page in the frame): its own keys are its own. */
function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || typeof el.tagName !== 'string') return false
  const tag = el.tagName.toLowerCase()
  return tag === 'textarea' || tag === 'input' || tag === 'select' || el.isContentEditable === true || !!el.closest?.('[contenteditable="true"], [contenteditable=""]')
}

export default function StoryEditor({ frameSrc, draft, onAskAgent, askOpen = false, onClose, aside, notices, asideWidth, title = 'Story board', runtime, onHelp, onPublishNew }: StoryEditorProps) {
  // Another live email holds the edit lease (#1578 item 5): nothing on the
  // page is editable, Save/Publish are disabled, and a banner says who.
  const readOnly = !!draft.lockedBy
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const mainRef = useRef<HTMLElement | null>(null)
  const docRef = useRef<StoryDoc | null>(null)
  /** The source the page in the iframe currently shows. When the draft moves away from it (undo, a section move, a restore), the page is remounted. */
  const domSourceRef = useRef<string | null>(null)
  const baselineRef = useRef<ProseBaseline | null>(null)
  const mountSeq = useRef(0)
  const detachRef = useRef<(() => void) | null>(null)
  const hoverClearTimer = useRef<number | null>(null)

  const [frameLoads, setFrameLoads] = useState(0)
  const [mounting, setMounting] = useState(false)
  const [mountError, setMountError] = useState<string | null>(null)
  const [parity, setParity] = useState(true)
  const [kit, setKit] = useState(false)
  const [boxErrors, setBoxErrors] = useState<string[]>([])
  const [baselineErrors, setBaselineErrors] = useState<string[]>([])
  const [hover, setHover] = useState<{ leaf: number; block: number; host: number } | null>(null)
  const [settingsFor, setSettingsFor] = useState<number | null>(null)
  const [selected, setSelected] = useState<Selection | null>(null)
  const [, setTick] = useState(0)
  const [versionsAnchor, setVersionsAnchor] = useState<HTMLElement | null>(null)
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  // Event handlers attached inside the iframe read the newest props here.
  // (Assigned below, once saveBlocked is known.)
  const live = useRef({ draft, runtime, onAskAgent, saveBlocked: false, parity: true })

  const frameWindow = () => iframeRef.current?.contentWindow ?? null

  async function mount(source: string) {
    const win = frameWindow()
    if (!win) return
    const seq = ++mountSeq.current
    domSourceRef.current = source
    setMounting(true)
    try {
      const doc = parseStory(source)
      const res = await mountStoryForEditing(win, doc, { runtime: live.current.runtime, readOnly: !!live.current.draft.lockedBy })
      if (seq !== mountSeq.current) return
      mainRef.current = res.main
      docRef.current = doc
      baselineRef.current = null
      setParity(res.parity)
      setKit(res.kit)
      setBoxErrors(res.errors)
      if (!live.current.draft.dirty) setBaselineErrors(res.errors)
      setMountError(null)
    } catch (e) {
      if (seq !== mountSeq.current) return
      setMountError(e instanceof Error ? e.message : String(e))
    } finally {
      if (seq === mountSeq.current) setMounting(false)
    }
  }

  // Remount when the draft moves away from what the page shows (a new frame,
  // undo/redo, a section operation, a restore, a reload). Typing does not
  // remount: the page already shows the typed text.
  useEffect(() => {
    if (!frameLoads || draft.source == null) return
    if (draft.source === domSourceRef.current) return
    setHover(null)
    setSelected(null)
    void mount(draft.source)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.source, frameLoads])

  // Re-stamp which top-level blocks are unsaved after every render: ordinals
  // shift on every edit (commitProse re-stamps them), so `draft.changedIdx`
  // is never cached against a previous mark.
  useEffect(() => {
    if (mainRef.current) markDirty(mainRef.current, draft.changedIdx)
  })

  // Flip every editable box's contenteditable live when the lease is taken
  // or given back (#1578), without a remount: the page stays where it was
  // scrolled and no in-flight edit is lost.
  useEffect(() => {
    if (mainRef.current) setContentEditable(mainRef.current, !readOnly)
  })

  useEffect(() => () => {
    detachRef.current?.()
    if (hoverClearTimer.current) window.clearTimeout(hoverClearTimer.current)
  }, [])

  // ---- Edits typed into the page ----------------------------------------
  function handleInput(target: Element) {
    const main = mainRef.current
    const doc = docRef.current
    if (!main || !doc) return
    const role = target.getAttribute(ROLE_ATTR)
    if (role === 'prose') {
      const res = commitProse(main, target, doc, baselineRef.current ?? undefined)
      if (res.refused) {
        // The page no longer matches its source (a locked figure moved).
        // Nothing is written; the page is put back as the source has it.
        setNotice('Figures, charts and tables stay where they are. To move one, ask the agent.')
        const current = live.current.draft.source
        domSourceRef.current = null
        if (current != null) void mount(current)
        return
      }
      docRef.current = res.doc
      // Stamps that no longer line up force a remount (the effect sees a mismatch).
      domSourceRef.current = res.stampsValid ? res.source : null
      live.current.draft.applyEdit(res.source, `prose:${idxOf(target)}`)
    } else if (role === 'attr') {
      const res = commitField(target, doc)
      docRef.current = res.doc
      domSourceRef.current = res.source
      live.current.draft.applyEdit(res.source, `attr:${target.getAttribute(HOST_ATTR)}:${target.getAttribute(FIELD_ATTR)}`)
    }
  }

  function editableOf(target: EventTarget | null): Element | null {
    const n = target as Node | null
    const el = !n ? null : n.nodeType === 1 ? (n as Element) : n.parentElement
    return el?.closest(`[${ROLE_ATTR}="prose"], [${ROLE_ATTR}="attr"]`) ?? null
  }

  function handleKey(e: KeyboardEvent | ReactKeyboardEvent, inFrame: boolean) {
    // Outside the page, a text field keeps its own shortcuts: undo in a
    // composer or a dialog's field undoes the typing there, not the story.
    if (!inFrame && isTextEntry(e.target)) return
    const d = live.current.draft
    const key = e.key.toLowerCase()
    // Another person holds the edit lease (#1578): undo/redo act on a draft
    // this reader may not change right now.
    if (isMod(e) && (key === 'z' || key === 'y') && d.lockedBy) return
    if (isMod(e) && key === 'z') {
      e.preventDefault()
      if (e.shiftKey) d.redo()
      else d.undo()
      return
    }
    if (isMod(e) && key === 'y') {
      e.preventDefault()
      d.redo()
      return
    }
    if (isMod(e) && key === 's') {
      e.preventDefault()
      // The same rules as the Save button.
      if (d.dirty && !d.saving && !live.current.saveBlocked && live.current.parity && !d.lockedBy) void d.save()
      return
    }
    if (!inFrame) return
    const editable = editableOf(e.target)
    if (!editable) return
    if (e.key === 'Enter') {
      // One element is one paragraph: Enter would make the browser invent
      // block markup. Shift+Enter is a line break inside prose.
      e.preventDefault()
      if (e.shiftKey && editable.getAttribute(ROLE_ATTR) === 'prose') {
        editable.ownerDocument.execCommand('insertLineBreak')
      }
    } else if (e.key === 'Escape') {
      ;(editable as HTMLElement).blur()
    }
  }

  // The 300ms grace before hover (and the lock badge or section toolbar it
  // shows) is dropped: leaving the iframe entirely, or leaving a badge drawn
  // over the leaf it describes, both go through this same deferred clear, so
  // a moving pointer never sees the badge blink out and back.
  function scheduleHoverClear() {
    if (hoverClearTimer.current) window.clearTimeout(hoverClearTimer.current)
    hoverClearTimer.current = window.setTimeout(() => setHover(null), 300)
  }

  function attachFrame() {
    detachRef.current?.()
    const win = frameWindow()
    const doc = win?.document
    if (!win || !doc) return
    // Cancel drags (they can carry a locked figure into another paragraph)
    // and edits that span two elements, before the browser changes the page.
    const onBeforeInput = (e: Event) => {
      if (shouldBlockInput(e as InputEvent)) {
        e.preventDefault()
        setNotice('Figures, charts and tables stay where they are, and each paragraph is edited on its own.')
      }
    }
    const onInput = (e: Event) => {
      const el = editableOf(e.target)
      if (el) handleInput(el)
    }
    const onFocusIn = (e: Event) => {
      const el = editableOf(e.target)
      if (!el || !docRef.current) return
      if (el.getAttribute(ROLE_ATTR) === 'prose') {
        baselineRef.current = proseBaseline(el, docRef.current)
        setSelected({ idx: idxOf(el), kind: 'prose' })
      } else {
        setSelected({ idx: Number(el.getAttribute(HOST_ATTR)), kind: 'field', field: el.getAttribute(FIELD_ATTR) ?? undefined })
      }
    }
    const onKeyDown = (e: KeyboardEvent) => handleKey(e, true)
    const onPaste = (e: ClipboardEvent) => {
      const el = editableOf(e.target)
      if (!el) return
      e.preventDefault()
      let text = e.clipboardData?.getData('text/plain') ?? ''
      if (el.getAttribute(ROLE_ATTR) === 'attr') text = text.replace(/\s+/g, ' ')
      doc.execCommand('insertText', false, text)
    }
    const onOver = (e: MouseEvent) => {
      const main = mainRef.current
      const sdoc = docRef.current
      if (!main || !sdoc) return
      const t = targetsOf(e.target, main, sdoc)
      setHover((h) => {
        if (h && h.leaf === t.leaf && h.block === t.block && h.host === t.host) return h
        // A pt-chart/pt-table lock badge is drawn inside its rect, so it can
        // occlude the leaf for a moment; a target with no leaf right after
        // showing one is deferred through the grace timer rather than
        // dropping the badge at once. Entering the badge itself (its own
        // onMouseEnter) cancels the deferred clear outright.
        if (h && h.leaf !== -1 && t.leaf === -1) {
          scheduleHoverClear()
          return h
        }
        if (hoverClearTimer.current) window.clearTimeout(hoverClearTimer.current)
        return t
      })
    }
    const onLeave = () => scheduleHoverClear()
    const onClick = (e: MouseEvent) => {
      const main = mainRef.current
      const sdoc = docRef.current
      if (!main || !sdoc) return
      const n = e.target as Node | null
      const el = !n ? null : n.nodeType === 1 ? (n as Element) : n.parentElement
      if (!el || !main.contains(el)) return
      if (el.closest(`[${ROLE_ATTR}="attr"]`)) return
      // A link inside prose is text to edit here, not somewhere to go.
      if (el.closest('a')) e.preventDefault()
      const t = targetsOf(el, main, sdoc)
      if (t.leaf !== -1) {
        // A locked box answers with its lock badge, not the provenance dialog.
        e.preventDefault()
        e.stopPropagation()
        setSelected({ idx: t.leaf, kind: 'leaf' })
      } else if (!editableOf(el) && t.block !== -1) {
        setSelected({ idx: t.block, kind: 'block' })
      }
    }
    const bump = () => setTick((n) => n + 1)
    doc.addEventListener('beforeinput', onBeforeInput)
    doc.addEventListener('input', onInput)
    doc.addEventListener('focusin', onFocusIn)
    doc.addEventListener('keydown', onKeyDown)
    doc.addEventListener('paste', onPaste)
    doc.addEventListener('mouseover', onOver)
    doc.documentElement.addEventListener('mouseleave', onLeave)
    doc.addEventListener('click', onClick, true)
    win.addEventListener('scroll', bump, { passive: true })
    win.addEventListener('resize', bump)
    detachRef.current = () => {
      doc.removeEventListener('beforeinput', onBeforeInput)
      doc.removeEventListener('input', onInput)
      doc.removeEventListener('focusin', onFocusIn)
      doc.removeEventListener('keydown', onKeyDown)
      doc.removeEventListener('paste', onPaste)
      doc.removeEventListener('mouseover', onOver)
      doc.documentElement.removeEventListener('mouseleave', onLeave)
      doc.removeEventListener('click', onClick, true)
      win.removeEventListener('scroll', bump)
      win.removeEventListener('resize', bump)
    }
  }

  function handleFrameLoad() {
    domSourceRef.current = null
    mainRef.current = null
    docRef.current = null
    attachFrame()
    setFrameLoads((n) => n + 1)
  }

  // ---- Section operations -----------------------------------------------
  function handleBlockOp(idx: number, op: 'up' | 'down' | 'hide' | 'show' | 'duplicate' | 'delete') {
    // Belt and braces: the section toolbar that calls this is not shown at
    // all while locked (readOnly, below), but a stray call must still refuse.
    if (readOnly) return
    const source = draft.source
    if (source == null) return
    const doc = parseStory(source)
    const next =
      op === 'up' ? moveBlock(source, idx, -1, doc)
      : op === 'down' ? moveBlock(source, idx, 1, doc)
      : op === 'hide' ? setHidden(source, idx, true, doc)
      : op === 'show' ? setHidden(source, idx, false, doc)
      : op === 'duplicate' ? duplicateBlock(source, idx, doc)
      : deleteBlock(source, idx, doc)
    draft.applyEdit(next)
  }

  // ---- Box settings ------------------------------------------------------
  function handleApplySettings(idx: number, values: Record<string, string>) {
    setSettingsFor(null)
    if (readOnly) return
    const source = draft.source
    if (source == null) return
    const next = setInputs(source, idx, values)
    if (next !== source) draft.applyEdit(next)
  }

  function askTarget(idx: number): StoryAskTarget | null {
    const doc = docRef.current
    const node = doc?.nodes[idx]
    if (!doc || !node || !node.loc) return null
    // A section heading is a field of the whole section: quote its start tag, not every box in it.
    const field = selected?.kind === 'field' && selected.idx === idx ? selected.field : undefined
    const headOnly = !!field && !isLeafTag(node.tag)
    const main = mainRef.current
    const el = field
      ? main?.querySelector(`[${HOST_ATTR}="${idx}"][${FIELD_ATTR}="${field}"]`)
      : main?.querySelector(`[${IDX_ATTR}="${idx}"]`)
    return { idx, tag: node.tag, excerpt: storyExcerpt(doc, idx, headOnly), label: storyElementLabel(doc, idx, el?.textContent ?? '', field) }
  }

  function openAsk(idx: number) {
    if (idx !== -1) setSelected((s) => (s && s.idx === idx ? s : { idx, kind: 'leaf' }))
    onAskAgent?.(idx === -1 ? null : askTarget(idx))
  }

  // The section a section control acts on, outlined in the page, while the
  // pointer or the focus is on the toolbar.
  function handleSpot(idx: number, on: boolean) {
    const main = mainRef.current
    const doc = docRef.current
    if (!main) return
    if (on && doc) spotlightSection(main, idx, sectionLabel(doc, idx))
    else clearSpotlight(main)
  }

  async function handlePublish() {
    setConfirmPublish(false)
    await draft.publish()
  }

  // ---- Overlay geometry --------------------------------------------------
  const doc = docRef.current
  const rectOf = (idx: number): DOMRect | null => {
    const el = mainRef.current?.querySelector(`[${IDX_ATTR}="${idx}"]`)
    return el ? el.getBoundingClientRect() : null
  }
  const toolbarBlock = hover && hover.block !== -1 ? hover.block : selected?.kind === 'block' ? selected.idx : -1
  const showToolbar = !!doc && parity && toolbarBlock !== -1 && isMovableBlock(doc, toolbarBlock) && !readOnly
  const leafIdx = hover && hover.leaf !== -1 ? hover.leaf : selected?.kind === 'leaf' ? selected.idx : -1
  const leafNode = doc && leafIdx !== -1 ? doc.nodes[leafIdx] : null
  const blockRect = showToolbar ? rectOf(toolbarBlock) : null
  const leafRect = leafNode ? rectOf(leafIdx) : null
  // A chart or table's badge is drawn inside its own rect (it has the room);
  // a figure's badge stays above or below it, as it always has.
  const isChartLike = leafNode?.tag === 'pt-chart' || leafNode?.tag === 'pt-table'

  // The box whose settings the chip opens: under the pointer, else around the selection.
  const settingsIdx = !doc ? -1 : hover && hover.host !== -1 ? hover.host : selected ? settingsHostOf(doc, selected.idx) : -1
  const settingsRect = doc && parity && kit && !readOnly && settingsIdx !== -1 ? rectOf(settingsIdx) : null
  const settingsName = doc && settingsIdx !== -1 ? SETTINGS_NAMES[doc.nodes[settingsIdx]?.tag] ?? 'Box' : 'Box'

  const newErrors = boxErrors.filter((e) => !baselineErrors.includes(e))
  const saveBlocked = newErrors.length > 0
  // The top-level block under the pointer or the caret, when it is one of
  // the blocks with unsaved edits: where the per-element Save overlay shows.
  const activeBlock = doc ? (hover && hover.block !== -1 ? hover.block : selected ? blockOf(doc, selected.idx) : -1) : -1
  const dirtyBlock = activeBlock !== -1 && parity && !saveBlocked && !readOnly && draft.changedIdx.includes(activeBlock) ? activeBlock : -1
  const dirtyRect = dirtyBlock !== -1 ? rectOf(dirtyBlock) : null
  live.current = { draft, runtime, onAskAgent, saveBlocked, parity }
  // Publish: warns before it would change what a link or dashboard shows,
  // confirms once it has, and is disabled while there is nothing to send or
  // a reason not to (dirty, blocked, already running, or someone else holds
  // the edit lease).
  const publishBlocked = draft.dirty || draft.publishing || draft.saving || saveBlocked || readOnly
  // Not in DataViz yet: Publish puts it there, through the host's dialog.
  const publishesNew = !!onPublishNew && !draft.publishTargets.some((t) => t.kind === 'dashboard' && t.can_update !== false)
  const publishState = publishesNew
    ? { label: 'Publish', color: 'info' as const, disabled: publishBlocked }
    : draft.draftDiffersFromPublished
    ? { label: 'Publish?', color: 'warning' as const, disabled: publishBlocked }
    : draft.publishedAt
      ? { label: 'Published', color: 'success' as const, disabled: true }
      : { label: 'Publish', color: 'info' as const, disabled: publishBlocked }

  if (draft.status === 'loading' || draft.status === 'idle') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 240 }}>
        <CircularProgress size={28} color="info" />
      </Box>
    )
  }
  if (draft.status === 'error') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">This story board could not be opened for editing: {draft.loadError}</Alert>
      </Box>
    )
  }

  const messages: ReactNode[] = []
  if (notice) {
    messages.push(
      <Alert key="notice" severity="info" onClose={() => setNotice(null)}>{notice}</Alert>,
    )
  }
  if (mountError) messages.push(<Alert key="mount" severity="error">The page could not be prepared for editing: {mountError}</Alert>)
  if (draft.lockedBy) {
    messages.push(
      <Alert key="locked" severity="warning" data-testid="story-locked-banner">
        {draft.lockedBy} is editing this at the moment.
      </Alert>,
    )
  }
  if (!parity) {
    messages.push(
      <Alert key="parity" severity="warning">
        This page is written in a way the editor cannot map safely, so it is shown read-only. Ask the agent to make the change instead.
      </Alert>,
    )
  }
  if (saveBlocked) {
    messages.push(
      <Alert key="box" severity="error">
        A box on the page can no longer draw, so this draft cannot be saved: {newErrors[0]}. Undo the last change to put it back.
      </Alert>,
    )
  }
  const s = draft.saveState
  if (s.kind === 'conflict') {
    messages.push(
      <Alert
        key="conflict"
        severity="warning"
        action={<Button color="info" size="small" onClick={() => void draft.reload()}>Load newest</Button>}
      >
        A newer version was saved since you opened this, by the agent or someone else. Load the newest version to carry on; your unsaved changes here will be dropped.
      </Alert>,
    )
  } else if (s.kind === 'invalid') {
    messages.push(
      <Alert key="invalid" severity="error">
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Not saved: {s.error}</Typography>
        {s.details.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {s.details.map((d) => <li key={d}>{d}</li>)}
          </Box>
        )}
      </Alert>,
    )
  } else if (s.kind === 'error') {
    messages.push(<Alert key="err" severity="error">Not saved: {s.message}</Alert>)
  } else if (s.kind === 'saved' && s.warnings.length > 0) {
    messages.push(
      <Alert key="warn" severity="info">
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Draft saved, with {s.warnings.length === 1 ? 'a note' : `${s.warnings.length} notes`}:</Typography>
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {s.warnings.map((w) => <li key={w}>{w}</li>)}
        </Box>
      </Alert>,
    )
  }
  if (draft.publishError) messages.push(<Alert key="puberr" severity="error">Not published: {draft.publishError}</Alert>)
  if (draft.publishResult) {
    const r = draft.publishResult
    messages.push(
      <Alert key="pub" severity={r.skipped.length ? 'warning' : 'success'}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          Published.{' '}
          {r.updated.length > 0 && `Updated ${r.updated.map((t) => t.name || `${t.kind} ${t.id}`).join(', ')}.`}
        </Typography>
        {r.skipped.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {r.skipped.map((t) => (
              <li key={`${t.kind}:${t.id}`}>Not updated: {t.name || `${t.kind} ${t.id}`}{t.reason ? ` (${t.reason})` : ''}</li>
            ))}
          </Box>
        )}
      </Alert>,
    )
  }

  const selectedForAsk = selected && doc?.nodes[selected.idx]?.loc ? selected.idx : -1

  return (
    <Box
      data-testid="story-editor"
      onKeyDown={(e) => handleKey(e, false)}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: 'background.paper', ...NO_SIDEWAYS_SCROLL }}
    >
      {/* ---- The bar ---- */}
      <Box data-testid="story-editor-bar" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, minHeight: 56, py: 0.75, borderBottom: HAIRLINE, flexWrap: 'wrap', flexShrink: 0 }}>
        {onClose && (
          <Tooltip title="Close the editor">
            <IconButton size="small" aria-label="Close editor" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Typography data-testid="story-editor-title" noWrap sx={{ fontWeight: 600, fontSize: 15, minWidth: 0, maxWidth: 360 }}>{title}</Typography>
        <Box data-testid="story-editor-undo-redo" sx={{ display: 'flex', ml: 1 }}>
          <Tooltip title="Undo (⌘Z)">
            <span>
              <IconButton size="small" aria-label="Undo" disabled={!draft.canUndo || readOnly} onClick={draft.undo}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Redo (⇧⌘Z)">
            <span>
              <IconButton size="small" aria-label="Redo" disabled={!draft.canRedo || readOnly} onClick={draft.redo}>
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          color="info"
          data-testid="story-editor-versions-button"
          startIcon={<HistoryIcon fontSize="small" />}
          onClick={(e) => setVersionsAnchor(e.currentTarget)}
          sx={{ borderRadius: PILL_RADIUS, textTransform: 'none' }}
        >
          Versions
        </Button>
        <Button
          size="small"
          variant={draft.dirty ? 'contained' : 'outlined'}
          color={draft.dirty ? 'warning' : 'success'}
          disableElevation
          data-testid="story-editor-save-button"
          disabled={draft.dirty ? draft.saving || saveBlocked || !parity || readOnly : true}
          onClick={() => void draft.save()}
          sx={{ borderRadius: PILL_RADIUS, textTransform: 'none' }}
        >
          {draft.saving ? 'Saving…' : draft.dirty ? 'Save all changes' : 'Saved'}
        </Button>
        <Tooltip
          title={publishState.label === 'Publish?' ? 'The saved draft is not what people with the link see. Publish to update them.' : ''}
          disableHoverListener={publishState.label !== 'Publish?'}
        >
          <span>
            <Button
              size="small"
              variant="contained"
              color={publishState.color}
              disableElevation
              data-testid="story-editor-publish-button"
              disabled={publishState.disabled}
              onClick={() => (publishesNew ? onPublishNew!() : setConfirmPublish(true))}
              sx={{ borderRadius: PILL_RADIUS, textTransform: 'none' }}
            >
              {draft.publishing ? 'Publishing…' : publishState.label}
            </Button>
          </span>
        </Tooltip>
        {onAskAgent && (
          <>
            <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.5, mx: 0.5, bgcolor: 'divider' }} />
            <Tooltip describeChild title={selectedForAsk === -1 ? 'Talk to the agent about this story board' : 'Ask the agent to change what you selected'}>
              <Button
                size="small"
                color="info"
                variant={askOpen ? 'contained' : 'text'}
                disableElevation
                aria-pressed={askOpen}
                data-testid="story-ask-agent"
                startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                onClick={() => openAsk(selectedForAsk)}
                sx={{ borderRadius: PILL_RADIUS, textTransform: 'none' }}
              >
                {selectedForAsk === -1 ? 'Ask the agent' : 'Ask the agent about this'}
              </Button>
            </Tooltip>
          </>
        )}
        {onHelp && (
          <Tooltip title="Show the story board editor walkthrough">
            <IconButton size="small" aria-label="Help" data-testid="story-editor-help-button" onClick={onHelp}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {mounting && <LinearProgress color="info" sx={{ height: 2 }} />}
      {!draft.draftSynced && (
        <Typography sx={{ px: 2, pt: 1, fontSize: 12, color: 'text.secondary' }}>
          The session is paused. The agent gets this draft with your next message.
        </Typography>
      )}
      {notices}
      {messages.length > 0 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, py: 1 }}>{messages}</Box>}

      {/* ---- The page, with the overlay drawn over it ---- */}
      <Box
        data-testid="story-editor-page-area"
        style={{ paddingRight: askOpen && asideWidth ? asideWidth : undefined }}
        // Clipped, not a scroll box: nothing in it (a panel sliding in or out,
        // a scrollIntoView, a focus) can scroll the page sideways.
        sx={{ position: 'relative', flex: 1, minHeight: 0, ...NO_SCROLL }}
      >
        <iframe
          ref={iframeRef}
          src={frameSrc}
          title={title}
          sandbox="allow-scripts allow-same-origin"
          onLoad={handleFrameLoad}
          style={{ width: '100%', height: '100%', border: 'none', backgroundColor: 'white', display: 'block' }}
        />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {showToolbar && doc && blockRect && (
            <SectionToolbar
              top={Math.max(6, blockRect.top + 6)}
              left={blockRect.right - 8}
              hidden={isHidden(doc, toolbarBlock)}
              canMoveUp={blockNeighbour(doc, toolbarBlock, -1) !== -1}
              canMoveDown={blockNeighbour(doc, toolbarBlock, 1) !== -1}
              label={sectionLabel(doc, toolbarBlock)}
              onOp={(op) => handleBlockOp(toolbarBlock, op)}
              onEnter={() => hoverClearTimer.current && window.clearTimeout(hoverClearTimer.current)}
              onSpot={(on) => handleSpot(toolbarBlock, on)}
            />
          )}
          {leafNode && leafRect && parity && (
            <LockBadge
              top={isChartLike ? leafRect.top + 8 : leafRect.top > 40 ? leafRect.top - 34 : leafRect.bottom + 6}
              left={isChartLike ? leafRect.left + 8 : Math.max(6, leafRect.left)}
              tag={leafNode.tag}
              onChangeViaAgent={onAskAgent ? () => openAsk(leafIdx) : undefined}
              onEnter={() => hoverClearTimer.current && window.clearTimeout(hoverClearTimer.current)}
              onLeave={scheduleHoverClear}
            />
          )}
          {settingsRect && (
            <SettingsChip
              // Under the section toolbar when that sits in the same corner.
              top={Math.max(6, settingsRect.top + 6) + (showToolbar && toolbarBlock === settingsIdx ? 40 : 0)}
              left={settingsRect.right - 8}
              label={`${settingsName} settings`}
              onOpen={() => setSettingsFor(settingsIdx)}
              onEnter={() => hoverClearTimer.current && window.clearTimeout(hoverClearTimer.current)}
            />
          )}
          {dirtyRect && (
            <DirtySaveOverlay
              top={dirtyRect.bottom + 6}
              left={Math.max(6, dirtyRect.left)}
              disabled={draft.saving}
              onSave={() => void draft.save()}
              onEnter={() => hoverClearTimer.current && window.clearTimeout(hoverClearTimer.current)}
            />
          )}
        </Box>
        {/* Keys typed in the host's panel stay there: the editor's undo,
            redo and save shortcuts are for the page. */}
        {/* The host's panel lives in its own clipped strip on the right, out of
            the page's flow: closed, it takes no room and nothing of it can
            reach past the page's edge; open, the page has given up exactly
            this width (asideWidth). Keys typed in it stay in it: the
            editor's undo, redo and save shortcuts are for the page. */}
        {aside && (
          <Box
            data-testid="story-editor-aside"
            onKeyDown={(e) => e.stopPropagation()}
            style={{ width: asideWidth ?? '100%' }}
            sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, pointerEvents: askOpen ? 'auto' : 'none', ...NO_SCROLL }}
          >
            {aside}
          </Box>
        )}
      </Box>

      <VersionsMenu
        anchor={versionsAnchor}
        onClose={() => setVersionsAnchor(null)}
        versions={draft.versions}
        latestVersionId={draft.latestVersionId}
        restoring={draft.restoring}
        onRestore={(id) => {
          setVersionsAnchor(null)
          void draft.restore(id)
        }}
      />

      {settingsFor !== null && doc && (
        <BoxSettingsDialog
          title={`${SETTINGS_NAMES[doc.nodes[settingsFor]?.tag] ?? 'Box'} settings`}
          inputs={blockInputs(doc, settingsFor)}
          pairs={STORY_VOCABULARY[doc.nodes[settingsFor]?.tag]?.pairs ?? []}
          onClose={() => setSettingsFor(null)}
          onApply={(values) => handleApplySettings(settingsFor, values)}
        />
      )}

      <Dialog open={confirmPublish} onClose={() => setConfirmPublish(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 17, fontWeight: 600 }}>Publish this story board?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            People with the link, and dashboards published from this session, will see this version.
            {draft.dirty && ' Your unsaved changes are saved first.'}
          </Typography>
          {draft.publishTargets.length > 0 && (
            <Box component="ul" data-testid="story-publish-targets" sx={{ m: 0, mt: 1.5, pl: 2.5, fontSize: 14 }}>
              {draft.publishTargets.map((t) => (
                <li key={`${t.kind}:${t.id}`}>
                  {t.name || `${t.kind} ${t.id}`}
                  {t.can_update === false && (
                    <Box component="span" sx={{ color: 'text.secondary' }}> (not updated: someone else published it)</Box>
                  )}
                </li>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setConfirmPublish(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="info" variant="contained" disableElevation onClick={() => void handlePublish()} sx={{ textTransform: 'none' }}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

const floatingSx = {
  position: 'absolute' as const,
  pointerEvents: 'auto' as const,
  bgcolor: 'background.paper',
  border: HAIRLINE,
  borderRadius: PILL_RADIUS,
  boxShadow: '0 4px 16px rgba(38,55,87,0.10)',
  display: 'flex',
  alignItems: 'center',
}

type BlockOp = 'up' | 'down' | 'hide' | 'show' | 'duplicate' | 'delete'

function SectionToolbar({ top, left, hidden, label, canMoveUp, canMoveDown, onOp, onEnter, onSpot }: {
  top: number
  left: number
  hidden: boolean
  /** "Section: In short": what every button here acts on. */
  label: string
  canMoveUp: boolean
  canMoveDown: boolean
  onOp: (op: BlockOp) => void
  onEnter: () => void
  /** The pointer or the focus arrived on (true) or left (false) the toolbar. */
  onSpot: (on: boolean) => void
}) {
  const btn = (label: string, op: BlockOp, icon: ReactNode, disabled = false) => (
    <Tooltip title={label} key={op}>
      <span>
        <IconButton size="small" aria-label={label} disabled={disabled} onClick={() => onOp(op)} sx={{ p: 0.6 }}>
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  )
  return (
    <Box
      data-testid="story-section-toolbar"
      role="toolbar"
      aria-label={label}
      onMouseEnter={() => { onEnter(); onSpot(true) }}
      onMouseLeave={() => onSpot(false)}
      onFocus={() => onSpot(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onSpot(false) }}
      sx={{ ...floatingSx, top, left, transform: 'translateX(-100%)', px: 0.5, py: 0.25, gap: 0.25 }}
    >
      {btn('Move section up', 'up', <ArrowUpwardIcon sx={{ fontSize: 18 }} />, !canMoveUp)}
      {btn('Move section down', 'down', <ArrowDownwardIcon sx={{ fontSize: 18 }} />, !canMoveDown)}
      {hidden
        ? btn('Show section', 'show', <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />)
        : btn('Hide section', 'hide', <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />)}
      {btn('Duplicate section', 'duplicate', <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />)}
      {btn('Delete section', 'delete', <DeleteOutlineIcon sx={{ fontSize: 18 }} />)}
    </Box>
  )
}

const LEAF_NAMES: Record<string, string> = { 'pt-num': 'Figure', 'pt-kpi': 'Figure', 'pt-chart': 'Chart', 'pt-table': 'Table' }

const LOCK_BADGE_TOOLTIP = "Numbers can't be edited — they come from the data (provenance). Ask the agent to change them."

/** One clickable pill: says what the box is, and, when the host offers it, hands it to the agent. */
function LockBadge({ top, left, tag, onChangeViaAgent, onEnter, onLeave }: {
  top: number
  left: number
  tag: string
  onChangeViaAgent?: () => void
  onEnter: () => void
  onLeave: () => void
}) {
  const badgeSx = { ...floatingSx, top, left, pl: 1, pr: 1.25, py: 0.5, gap: 0.75, font: 'inherit', maxWidth: 'min(420px, 90%)' } as const
  const content = (
    <>
      <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0 }} />
      <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary', minWidth: 0 }}>
        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>{LEAF_NAMES[tag] ?? 'Box'} from the data</Box>
        {onChangeViaAgent && ' · Change via agent'}
      </Typography>
    </>
  )
  // With no agent to hand the box to, the badge is informational only: a
  // disabled button would also stop the browser firing the hover events the
  // tooltip and the occlusion grace period need.
  if (!onChangeViaAgent) {
    return (
      <Tooltip title={LOCK_BADGE_TOOLTIP}>
        <Box data-testid="story-lock-badge" onMouseEnter={onEnter} onMouseLeave={onLeave} sx={badgeSx}>
          {content}
        </Box>
      </Tooltip>
    )
  }
  return (
    <Tooltip title={LOCK_BADGE_TOOLTIP}>
      <ButtonBase data-testid="story-lock-badge" onClick={onChangeViaAgent} onMouseEnter={onEnter} onMouseLeave={onLeave} sx={badgeSx}>
        {content}
      </ButtonBase>
    </Tooltip>
  )
}

/** The chip on a box with named inputs: opens its settings. */
function SettingsChip({ top, left, label, onOpen, onEnter }: {
  top: number
  left: number
  label: string
  onOpen: () => void
  onEnter: () => void
}) {
  return (
    <ButtonBase
      data-testid="story-settings-chip"
      aria-label={label}
      onClick={onOpen}
      onMouseEnter={onEnter}
      sx={{ ...floatingSx, top, left, transform: 'translateX(-100%)', pl: 1, pr: 1.25, py: 0.5, gap: 0.5, font: 'inherit' }}
    >
      <TuneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Settings</Typography>
    </ButtonBase>
  )
}

/** Why an input's value cannot be applied, or null. */
function inputProblem(input: BlockInput, value: string): string | null {
  const v = value.trim()
  if (!v) return input.required ? 'Required' : null
  if (input.type === 'image' && !imageOk(v)) return `Use a picture file inside the app directory, e.g. assets/hero.webp: a published page loads nothing from the internet`
  if (input.type === 'href' && !hrefOk(v)) return `Use ${HREF_HINT.charAt(0).toLowerCase()}${HREF_HINT.slice(1)}`
  return null
}

/**
 * Every named input of one box, in one form: the words typed in place on the
 * page are here too (so an input not on the page yet can be added), and so
 * are the ones a page does not show as words (the picture, the tone, where
 * the button goes). Apply writes only what changed, into the box's start tag.
 */
function BoxSettingsDialog({ title, inputs, pairs, onClose, onApply }: {
  title: string
  inputs: BlockInput[]
  /** Inputs that come together or not at all (a button and where it goes). */
  pairs: [string, string][]
  onClose: () => void
  onApply: (values: Record<string, string>) => void
}) {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(inputs.map((i) => [i.name, i.value])))
  const filled = (name: string) => (values[name] ?? '').trim() !== ''
  const partner = (name: string) => pairs.flatMap(([a, b]) => (a === name ? [b] : b === name ? [a] : []))[0]
  const problems = inputs.map((i) => {
    const other = partner(i.name)
    if (other && !filled(i.name) && filled(other)) return `Needed with ${INPUT_LABELS[other] ?? other}`
    return inputProblem(i, values[i.name] ?? '')
  })
  const changed = Object.fromEntries(inputs.filter((i) => (values[i.name] ?? '') !== i.value).map((i) => [i.name, values[i.name] ?? '']))
  const set = (name: string, value: string) => setValues((v) => ({ ...v, [name]: value }))
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="story-box-settings-title">
      <DialogTitle id="story-box-settings-title" sx={{ fontSize: 17, fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {inputs.map((input, n) => {
          const label = INPUT_LABELS[input.name] ?? input.name
          const hint = input.type === 'image' ? IMAGE_HINT : input.type === 'href' ? HREF_HINT : undefined
          if (input.type === 'choice') {
            return (
              <TextField
                key={input.name}
                select
                size="small"
                label={label}
                value={values[input.name] ?? ''}
                onChange={(e) => set(input.name, e.target.value)}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                {!input.required && <option value="">{`Default (${input.options?.[0] ?? ''})`}</option>}
                {input.options?.map((o) => <option key={o} value={o}>{o}</option>)}
              </TextField>
            )
          }
          return (
            <TextField
              key={input.name}
              size="small"
              label={label}
              value={values[input.name] ?? ''}
              onChange={(e) => set(input.name, e.target.value)}
              error={!!problems[n]}
              helperText={problems[n] ?? hint}
            />
          )
        })}
      </DialogContent>
      <DialogActions>
        <Button color="info" onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          color="info"
          variant="contained"
          disableElevation
          disabled={problems.some(Boolean) || Object.keys(changed).length === 0}
          onClick={() => onApply(changed)}
          sx={{ textTransform: 'none' }}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** The per-element Save overlay (#1578): sits on the hovered or focused block with unsaved edits; always saves the whole draft. */
function DirtySaveOverlay({ top, left, disabled, onSave, onEnter }: {
  top: number
  left: number
  disabled: boolean
  onSave: () => void
  onEnter: () => void
}) {
  return (
    <Box data-testid="story-dirty-save" onMouseEnter={onEnter} sx={{ ...floatingSx, top, left, px: 0.5, py: 0.5 }}>
      <Button
        size="small"
        color="warning"
        variant="contained"
        disableElevation
        disabled={disabled}
        onClick={onSave}
        sx={{ borderRadius: PILL_RADIUS, textTransform: 'none', fontSize: 12, py: 0.25 }}
      >
        Save all changes
      </Button>
    </Box>
  )
}

function VersionsMenu({ anchor, onClose, versions, latestVersionId, restoring, onRestore }: {
  anchor: HTMLElement | null
  onClose: () => void
  versions: StoryVersion[]
  latestVersionId: StoryVersion['id'] | null
  restoring: boolean
  onRestore: (id: StoryVersion['id']) => void
}) {
  return (
    <Menu
      anchorEl={anchor}
      open={!!anchor}
      onClose={onClose}
      slotProps={{ paper: { sx: { borderRadius: CARD_RADIUS, border: HAIRLINE, minWidth: 320, maxHeight: 420 } } }}
    >
      {versions.length === 0 && (
        <MenuItem disabled sx={{ fontSize: 13 }}>No saved versions yet</MenuItem>
      )}
      {versions.map((v) => {
        const current = String(v.id) === String(latestVersionId)
        return (
          <MenuItem
            key={String(v.id)}
            disableRipple
            sx={{ display: 'flex', gap: 1.5, alignItems: 'center', cursor: 'default', '&:hover': { bgcolor: 'transparent', borderColor: HAIRLINE_HOVER_COLOR } }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                {formatWhen(v.created_at)} · {v.created_by || 'unknown'}
              </Typography>
              {v.note && <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>{v.note}</Typography>}
            </Box>
            {current ? (
              <Chip size="small" label="Current" variant="outlined" sx={{ height: 20, fontSize: 11, borderRadius: PILL_RADIUS }} />
            ) : (
              <Button
                size="small"
                color="info"
                disabled={restoring}
                onClick={() => onRestore(v.id)}
                sx={{ borderRadius: PILL_RADIUS, textTransform: 'none', fontSize: 12 }}
              >
                Restore
              </Button>
            )}
          </MenuItem>
        )
      })}
    </Menu>
  )
}
